import { useEffect, useState } from 'react';
import Chip from '@material-ui/core/Chip';
import Tooltip from '@material-ui/core/Tooltip';
import {
  EmptyState,
  Link,
  StatusAborted,
  StatusError,
  StatusOK,
  StatusPending,
  StatusRunning,
  StatusWarning,
  Table,
  type TableColumn,
} from '@backstage/core-components';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';

import type { LiveRun } from './api';

/**
 * The group whose members may see every team's migration state. Mirrors the
 * check in `permissionPolicy.ts` and in the migration backend.
 *
 * This is a PRESENTATION check only — it decides what to render. The gate is
 * enforced server-side by the backend, which does not trust the client.
 */
export const DEVOPS_ADMINS = 'group:devops/admins';

export function useIsDevopsAdmin(): { loading: boolean; isAdmin: boolean } {
  const identityApi = useApi(identityApiRef);
  const [state, setState] = useState({ loading: true, isAdmin: false });

  useEffect(() => {
    let live = true;
    identityApi
      .getBackstageIdentity()
      .then(id => {
        if (live) {
          setState({
            loading: false,
            isAdmin: (id.ownershipEntityRefs ?? []).includes(DEVOPS_ADMINS),
          });
        }
      })
      .catch(() => live && setState({ loading: false, isAdmin: false }));
    return () => {
      live = false;
    };
  }, [identityApi]);

  return state;
}

/** Human label for a durable migration status. */
export function statusLabel(status: string): string {
  switch (status) {
    case 'not-started':
      return 'Not started';
    case 'rolled-back':
      return 'Rolled back';
    default:
      return status
        .replace(/^step(\d)-/, 'Step $1 ')
        .replace(/^(\w)/, c => c.toUpperCase());
  }
}

/** Human labels for the exact statuses accepted by a service picker. */
export function statusFilterLabel(filter: string): string {
  return filter
    .split(',')
    .map(status => status.trim())
    .filter(Boolean)
    .map(statusLabel)
    .join(' or ');
}

/** Status indicator matching the migration's own vocabulary. */
export function MigrationStatus({ status }: { status: string }) {
  const label = statusLabel(status);
  if (status.endsWith('-failed')) return <StatusError>{label}</StatusError>;
  if (status.endsWith('-running'))
    return <StatusRunning>{label}</StatusRunning>;
  if (status === 'step3-completed') return <StatusOK>{label}</StatusOK>;
  if (status.endsWith('-skipped') || status === 'mixed')
    return <StatusWarning>{label}</StatusWarning>;
  if (status.endsWith('-completed'))
    return <StatusPending>{label}</StatusPending>;
  if (status === 'rolled-back') return <StatusWarning>{label}</StatusWarning>;
  return <StatusAborted>{label}</StatusAborted>;
}

/** An active database run with optional Argo link and progress. */
export function LiveRunChip({ run }: { run: LiveRun }) {
  const label = `${run.step} ${run.phase}${run.progress ? ` ${run.progress}` : ''}`;
  const chip = (
    <Chip
      size="small"
      label={label}
      color={
        ['running', 'pending', 'Running', 'Pending'].includes(run.phase)
          ? 'primary'
          : 'default'
      }
      {...(run.url
        ? { component: 'a', href: run.url, target: '_blank', clickable: true }
        : {})}
    />
  );
  return (
    <Tooltip title={run.url ? 'Open in Argo Workflows' : run.name}>
      <span>{chip}</span>
    </Tooltip>
  );
}

/** Human label for a durable database run status. */
export function RunPhase({ phase }: { phase: string }) {
  switch (phase) {
    case 'completed':
    case 'Succeeded':
      return <StatusOK>Succeeded</StatusOK>;
    case 'running':
    case 'Running':
      return <StatusRunning>Running</StatusRunning>;
    case 'pending':
    case 'Pending':
      return <StatusPending>Pending</StatusPending>;
    case 'failed':
    case 'Failed':
    case 'Error':
      return <StatusError>{phase}</StatusError>;
    default:
      return <StatusAborted>{phase}</StatusAborted>;
  }
}

/** `step1` -> `Step 1`, `rollback` -> `Rollback`, anything else unchanged. */
export function stepLabel(step: string): string {
  return step
    .replace(/^step(\d)$/, 'Step $1')
    .replace(/^(\w)/, c => c.toUpperCase());
}

/** `2026-09-24T06:25:31Z` + finish -> `4m 12s`, or `—` while still running. */
export function runDuration(run: LiveRun): string {
  if (!run.startedAt) return '—';
  const end = run.finishedAt ? Date.parse(run.finishedAt) : Date.now();
  const secs = Math.max(
    0,
    Math.round((end - Date.parse(run.startedAt)) / 1000),
  );
  if (!Number.isFinite(secs)) return '—';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  let out = `${s}s`;
  if (h > 0) out = `${h}h ${m}m`;
  else if (m > 0) out = `${m}m ${s}s`;
  return run.finishedAt ? out : `${out}…`;
}

/** Database migration run history, including local execution and dry runs. */
export function MigrationRunsTable({
  runs,
  title,
  description,
}: {
  runs: LiveRun[];
  title: string;
  description: string;
}) {
  const columns: TableColumn<LiveRun>[] = [
    {
      title: 'Mode',
      field: 'dryRun',
      render: row => (row.dryRun ? 'Dry run' : 'Live'),
    },
    {
      title: 'Step',
      field: 'step',
      width: '120px',
      render: row => stepLabel(row.step),
    },
    {
      title: 'Phase',
      field: 'phase',
      width: '140px',
      render: row => <RunPhase phase={row.phase} />,
    },
    {
      title: 'Progress',
      field: 'progress',
      width: '110px',
      sorting: false,
      render: row => row.progress ?? '—',
    },
    {
      title: 'Started',
      field: 'startedAt',
      defaultSort: 'desc',
      render: row =>
        row.startedAt ? new Date(row.startedAt).toLocaleString() : '—',
    },
    {
      title: 'Duration',
      field: 'finishedAt',
      width: '110px',
      sorting: false,
      render: row => runDuration(row),
    },
    {
      title: 'Workflow',
      field: 'name',
      sorting: false,
      render: row =>
        row.url ? (
          <Link to={row.url} target="_blank" rel="noopener">
            Open in Argo
          </Link>
        ) : (
          <Tooltip title={row.name}>
            <span>{row.name}</span>
          </Tooltip>
        ),
    },
  ];

  if (runs.length === 0) {
    return (
      <EmptyState
        missing="data"
        title="No migration runs"
        description={description}
      />
    );
  }

  return (
    <Table<LiveRun>
      title={`${title} (${runs.length})`}
      columns={columns}
      data={runs}
      options={{ pageSize: 10, pageSizeOptions: [10, 25, 50], search: false }}
    />
  );
}

/** Shown when the backend could not reach Argo. */
export function liveStatusNote(available: boolean): string | undefined {
  return available
    ? undefined
    : 'Argo details are unavailable — database status and run history remain available. ' +
        'Check that the Backstage service account has "list" on argoproj.io/workflows.';
}
